import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  reserveSpot,
  getReserveSpotError,
} from './reserveSpot';
import {
  executeBookSpot,
  getSelectedSpotKeyForMap,
  handleInteractiveSpotSelect,
  getButtonActionMode,
  requiresSpotReselection,
  RoomLayoutErrorCode,
  type RoomLayoutMapProps,
  type RoomLayoutSpot,
  type RoomLayoutError,
  type ButtonActionMode,
  type BookableAvailability,
} from '@glofoxinc/room-layout-map/interactiveSpotBooking';
import { createTrackEvent } from '../../clients/analytics';
import { InteractiveSpotEvent } from '../../types/analytics';
import { getSpotEventContext, type SpotEventContext } from './spotBookingMetrics';
import type { BookSpotData } from './BookSpot.types';

export type UseBookSpotReturn = {
  selectedSpot: RoomLayoutSpot | null;
  transactionInProgress: boolean;
  hasSucceeded: boolean;
  error: boolean;
  layoutUnavailable: boolean;
  layoutError: RoomLayoutError | null;
  skipLoadingAvailable: boolean;
  spotTaken: boolean;
  trackSpotEvent: (
    name: InteractiveSpotEvent,
    spot?: SpotEventContext | null,
    extra?: Record<string, unknown>
  ) => void;
  roomLayoutMapData: RoomLayoutMapProps;
  handleClose: () => void;
  handleCloseFromSpotSelection: () => void;
  bookSpot: () => void;
  skipBooking: () => void;
  confirm: () => void;
  buttonActionMode: ButtonActionMode;
  layoutWarningLabels: () => { title?: string; message?: string };
};

export function useBookSpot(
  data: BookSpotData,
  renderLoading: () => ReactNode
): UseBookSpotReturn {
  const {
    envUrl,
    userToken,
    layoutId,
    timeSlotId,
    bookingId,
    userId,
    layoutErrorTitle,
    layout404Message,
    layoutUnavailableTitle,
    layoutUnavailableMessage,
    onClose,
    onSuccess,
    trackEvent,
  } = data;

  const track = useMemo(() => createTrackEvent(trackEvent), [trackEvent]);

  const trackSpotEvent = useCallback(
    (name: InteractiveSpotEvent, spot?: SpotEventContext | null, extra?: Record<string, unknown>) => {
      const metadata: Record<string, unknown> = { timeSlotId, bookingId, userId, ...extra };
      if (spot) {
        metadata.spotTypePrefix = spot.spotTypePrefix;
        metadata.spotNumber = spot.spotNumber;
      }
      track({ name, category: 'source:event', metadata });
    },
    [track, timeSlotId, bookingId, userId]
  );

  const [selectedSpot, setSelectedSpot] = useState<RoomLayoutSpot | null>(null);
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  const [error, setError] = useState(false);
  const isMapComponentAvailable =
    typeof window === 'undefined' || !!window.WebReactComponents?.RoomLayoutMap;
  const [layoutUnavailable, setLayoutUnavailable] = useState(!isMapComponentAvailable);
  const [layoutError, setLayoutError] = useState<RoomLayoutError | null>(
    isMapComponentAvailable ? null : { code: RoomLayoutErrorCode.CONNECTION_ERROR }
  );
  const [skipLoadingAvailable, setSkipLoadingAvailable] = useState(false);
  const [spotTaken, setSpotTaken] = useState(false);
  const [hasBookableSpots, setHasBookableSpots] = useState<boolean | null>(null);
  const mapRefetchRef = useRef<(() => void) | null>(null);
  // The shared library uses skipInFlightRef to block spot taps and booking while an
  // async skip is running. The portal's skipBooking() is fully synchronous (the booking
  // is already created by the Angular host, so no reserveSpot call happens), so a skip is
  // never in flight and this ref stays false for the modal's lifetime. It exists only
  // because handleInteractiveSpotSelect requires it. Do not try to plumb it.
  const skipInFlightRef = useRef(false);
  // Latches once onSuccess has fired so a slow host modal transition can't let the
  // user re-trigger a reserve/skip or fire onSuccess a second time before it dismisses.
  const hasSucceededRef = useRef(false);
  const [hasSucceeded, setHasSucceeded] = useState(false);

  const handleSuccess = useCallback(() => {
    if (hasSucceededRef.current) {
      return;
    }
    hasSucceededRef.current = true;
    setHasSucceeded(true);
    if (onSuccess) {
      onSuccess();
    }
  }, [onSuccess]);

  const handleSpotSelect = useCallback(
    (spot: RoomLayoutSpot) => {
      setSpotTaken(false);
      setError(false);
      handleInteractiveSpotSelect(
        spot,
        timeSlotId,
        ({ spotNumber, spotTypePrefix, isBookable }) =>
          trackSpotEvent(InteractiveSpotEvent.SPOT_TAPPED, { spotTypePrefix, spotNumber }, { isBookable }),
        setSelectedSpot,
        skipInFlightRef
      );
    },
    [timeSlotId, trackSpotEvent]
  );

  const handleRefetchReady = useCallback((refetch: () => void) => {
    mapRefetchRef.current = refetch;
  }, []);

  const handleMapError = useCallback(
    (mapError: unknown) => {
      trackSpotEvent(InteractiveSpotEvent.ROOM_LAYOUT_MAP_LOAD_ERROR, null, {
        message: mapError instanceof Error ? mapError.message : String(mapError),
      });
    },
    [trackSpotEvent]
  );

  const handleErrorStateChange = useCallback((isError: boolean, mapError: RoomLayoutError | null) => {
    setLayoutUnavailable(!!isError);
    setLayoutError(mapError || null);
    if (!isError) {
      setSkipLoadingAvailable(false);
    }
  }, []);

  const handleSkipLoadingAvailable = useCallback((available: boolean) => {
    setSkipLoadingAvailable(!!available);
  }, []);

  const handleBookableAvailabilityChange = useCallback((info: BookableAvailability) => {
    setHasBookableSpots(info.hasBookableSpots);
  }, []);

  const roomLayoutMapData = useMemo<RoomLayoutMapProps>(
    () => ({
      envUrl,
      userToken,
      layoutId,
      timeSlotId,
      selectedSpotKey: getSelectedSpotKeyForMap(selectedSpot),
      onSpotSelect: handleSpotSelect,
      onError: handleMapError,
      onErrorStateChange: handleErrorStateChange,
      onSkipLoadingAvailable: handleSkipLoadingAvailable,
      onBookableAvailabilityChange: handleBookableAvailabilityChange,
      onRefetchReady: handleRefetchReady,
      renderLoading,
    }),
    [
      envUrl,
      userToken,
      layoutId,
      timeSlotId,
      selectedSpot,
      handleSpotSelect,
      handleMapError,
      handleErrorStateChange,
      handleSkipLoadingAvailable,
      handleBookableAvailabilityChange,
      handleRefetchReady,
      renderLoading,
    ]
  );

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // The booking is already created by the Angular host before this dialog opens,
  // so dismissing the spot selection still ends in a confirmed booking: hand over
  // to the success dialog instead of silently closing.
  const handleCloseFromSpotSelection = useCallback(() => {
    if (transactionInProgress || hasSucceededRef.current) {
      return;
    }
    handleSuccess();
  }, [transactionInProgress, handleSuccess]);

  const layoutWarningLabels = useCallback(() => {
    // NOT_FOUND gets dedicated copy. Every other RoomLayoutErrorCode
    // (LAYOUT_INACTIVE, CONNECTION_ERROR, SERVER_ERROR, etc.) intentionally falls
    // back to the generic "unavailable" message, since there is no dedicated UI
    // path per error code.
    const is404 = !!layoutError && layoutError.code === RoomLayoutErrorCode.NOT_FOUND;
    return is404
      ? {
          title: layoutErrorTitle,
          message: layout404Message,
        }
      : {
          title: layoutUnavailableTitle,
          message: layoutUnavailableMessage,
        };
  }, [layoutError, layoutErrorTitle, layout404Message, layoutUnavailableTitle, layoutUnavailableMessage]);

  const buttonActionMode = useMemo(
    () =>
      getButtonActionMode({
        bookingId,
        selectedSpot: selectedSpot ? { spotName: getSelectedSpotKeyForMap(selectedSpot) ?? '' } : null,
        layoutError: layoutUnavailable ? layoutError : null,
        skipLoadingAvailable,
        bookSpotError: error ? { message: '' } : null,
        hasBookableSpots,
        isInteractiveSpotBookingEnabled: true,
        // Always false here: the portal's skipBooking() is synchronous, so a skip is
        // never in flight (see skipInFlightRef above).
        isSkipInFlight: false,
      }),
    [bookingId, selectedSpot, layoutUnavailable, layoutError, skipLoadingAvailable, error, hasBookableSpots]
  );

  const bookSpot = useCallback(() => {
    if (!selectedSpot || transactionInProgress || hasSucceededRef.current) {
      return;
    }

    const spotContext = getSpotEventContext(selectedSpot);

    executeBookSpot(selectedSpot, false, false, {}, {
      setQuickBookPendingClose: () => {},
      setLastBookSpotContext: () => {},
      trackSpotBookingCtaClicked: () => {},
      trackSpotBookingRequestSent: (context) => {
        trackSpotEvent(InteractiveSpotEvent.BOOKING_REQUEST_SENT, context);
      },
      trackInteractiveSpotChangeRequestSent: () => {},
      setLastChangeSpotContext: () => {},
      bookSpot: ({ bookingId: reserveBookingId, spotPayload }) => {
        setTransactionInProgress(true);
        setError(false);
        setSpotTaken(false);

        reserveSpot(envUrl, userToken, reserveBookingId, spotPayload)
          .then(() => {
            trackSpotEvent(InteractiveSpotEvent.BOOKING_SPOT_RESERVED, spotContext);
            handleSuccess();
          })
          .catch((reserveError: unknown) => {
            const normalized = getReserveSpotError(reserveError, 'WEB_BOOK_SPOT_ERROR');
            trackSpotEvent(InteractiveSpotEvent.BOOKING_ERROR, spotContext, { errorCode: normalized.code });
            setSelectedSpot(null);
            if (requiresSpotReselection(reserveError)) {
              setSpotTaken(true);
              try {
                mapRefetchRef.current?.();
              } catch (refetchError: unknown) {
                console.log('room-layout-map refetch error => ', refetchError);
              }
              return;
            }
            setError(true);
          })
          .finally(() => {
            setTransactionInProgress(false);
          });
      },
      bookingId,
      eventId: timeSlotId,
      isChangeSpotFlow: false,
      previousSpotId: '',
    });
  }, [selectedSpot, transactionInProgress, envUrl, userToken, bookingId, timeSlotId, trackSpotEvent, handleSuccess]);

  const skipBooking = useCallback(() => {
    if (transactionInProgress || hasSucceededRef.current) {
      return;
    }
    // The booking is already created by the Angular host before this dialog opens
    // (bookingId is passed in), so skipping simply confirms it without reserving a
    // spot — no reserveSpot call is needed.
    setError(false);
    trackSpotEvent(InteractiveSpotEvent.BOOKING_SPOT_SKIPPED, null);
    handleSuccess();
  }, [transactionInProgress, trackSpotEvent, handleSuccess]);

  const confirm = useCallback(() => {
    if (transactionInProgress || hasSucceededRef.current) {
      return;
    }
    trackSpotEvent(
      InteractiveSpotEvent.BOOKING_CTA_TAPPED,
      selectedSpot ? getSpotEventContext(selectedSpot) : null,
      { mode: buttonActionMode }
    );
    if (buttonActionMode === 'book') {
      bookSpot();
      return;
    }
    if (buttonActionMode === 'skip') {
      skipBooking();
    }
  }, [transactionInProgress, buttonActionMode, selectedSpot, bookSpot, skipBooking, trackSpotEvent]);

  return {
    selectedSpot,
    transactionInProgress,
    hasSucceeded,
    error,
    layoutUnavailable,
    layoutError,
    skipLoadingAvailable,
    spotTaken,
    trackSpotEvent,
    roomLayoutMapData,
    handleClose,
    handleCloseFromSpotSelection,
    bookSpot,
    skipBooking,
    confirm,
    buttonActionMode,
    layoutWarningLabels,
  };
}
